import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { getOverAllCleanseRules, createCopyCleanseRule } from '../../../../api\'s/DataModelApi';

const CopyRuleModal = ({ open, onClose, entityId, attributes = [], onSuccess }) => {
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) fetchRules();
  }, [open]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await getOverAllCleanseRules();
      setRules(data || []);
    } catch (error) {
      console.error('Error fetching overall rules:', error);
      enqueueSnackbar('Failed to load rules', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRuleSelect = (rule) => {
    setSelectedRule(rule);
    setSelectedAttributes([]); 
  };

  const toggleAttribute = (attrName) => {
    setSelectedAttributes((prev) =>
      prev.includes(attrName)
        ? prev.filter((a) => a !== attrName)
        : [...prev, attrName]
    );
  };

  const handleSave = async () => {
    if (!selectedRule || selectedAttributes.length === 0) {
      enqueueSnackbar('Please select a rule and at least one attribute.', {
        variant: 'warning',
      });
      return;
    }

    const payload = {
      entityId,
      cleanseRuleId: selectedRule.ruleId,
      appliesTo: selectedAttributes,
    };

    try {
      await createCopyCleanseRule(payload);
      enqueueSnackbar('Rule copied successfully!', { variant: 'success' });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error copying rule:', error);
      enqueueSnackbar('Failed to copy rule', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Existing Cleanse Rule</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={30} />
          </Box>
        ) : !selectedRule ? (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select a rule to copy:
            </Typography>
            {rules.map((rule) => (
              <Button
                key={rule.ruleId}
                variant="outlined"
                fullWidth
                sx={{
                  justifyContent: 'flex-start',
                  mb: 1,
                  textTransform: 'none',
                }}
                onClick={() => handleRuleSelect(rule)}
              >
                {rule.ruleName}
              </Button>
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              {selectedRule.ruleName}
            </Typography>
            <Typography variant="body2" gutterBottom>
              Select attributes to apply this rule:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {attributes.map((attr) => (
                <Chip
                  key={attr.name}
                  label={attr.name}
                  color={
                    selectedAttributes.includes(attr.name)
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() => toggleAttribute(attr.name)}
                  variant={
                    selectedAttributes.includes(attr.name)
                      ? 'filled'
                      : 'outlined'
                  }
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {selectedRule && (
          <Button onClick={() => setSelectedRule(null)}>Back</Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        {selectedRule && (
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopyRuleModal;
